import { colors, Heading, HeadingProps, LinkBox, LinkBoxProps, ListItem, VStack } from "@biblioteksentralen/react";
import { sift } from "radash";
import { ReactNode, useEffect, useRef } from "react";
import InternalLinkOverlay from "../../../components/InternalLinkOverlay";
import { useCommonData } from "../../../components/layout/CommonDataProvider";
import { getPublicationUrl } from "../../../utils/getPublicationUrl";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { Collections } from "../Collections";
import { DocumentTypes } from "../DocumentTypes";
import { ItemCoverImage } from "../ItemCoverImage";
import { TruncatedList } from "../TruncatedList";
import { GridItem, itemIsWork } from "./ItemGrid";

type ItemPreviewProps = {
  item: GridItem;
  headingProps?: HeadingProps;
  childrenTop?: ReactNode;
  childrenBottom?: ReactNode;
  autoFocus?: boolean;
  compact?: boolean;
} & Omit<LinkBoxProps, "children">;

const ItemPreview = ({
  item,
  headingProps,
  childrenBottom,
  childrenTop,
  autoFocus,
  compact,
  ...chakraProps
}: ItemPreviewProps) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const { lang } = useTranslation();
  const { site } = useCommonData();
  useEffect(() => {
    autoFocus && ref.current?.focus();
  }, [autoFocus]);

  const isWork = itemIsWork(item);
  const work = isWork ? item : undefined;
  const workTriple = isWork ? work?.representativeWorkTriple ?? work?.getWorkTriples()[0] : undefined;
  const publication = isWork ? undefined : item._type === "publication" ? item : item.publication;
  const mainContributorAgent = isWork
    ? work?.mainContributor?.agent
    : publication?.author && { name: publication.author };
  const documentTypes = itemIsWork(item) ? work?.getDocumentTypes() : undefined;
  const documentTypeLabels = itemIsWork(item) ? undefined : publication?.mediaTypes;
  const id = isWork ? work?.id : publication?.workId;
  const path = isWork ? work?.getWorkTriples()[0]?.workTriplePath : getPublicationUrl({ publication, site });
  const representativeTitleData = isWork ? work?.getRepresentativeTitleData() : undefined;
  const title = isWork
    ? sift([representativeTitleData?.formattedTitle, ...(workTriple?.parallelTitles ?? [])]).join(" | ")
    : publication?.title;
  const formattedLanguages = work?.getFormattedLanguages({ capitalizeFirstLetters: "first" });

  if (!path) return null;

  return (
    <LinkBox
      as={ListItem}
      display="flex"
      // row-reverse to get focus on the title before the favorite button of the work
      flexDirection="row-reverse"
      alignItems="flex-end"
      justifyContent="flex-end"
      role="group"
      lineHeight="1.2"
      height="100%"
      minHeight="11.5rem"
      isolation="isolate"
      {...chakraProps}
    >
      <VStack marginLeft="0.5rem" alignItems="flex-start" spacing="0.25rem" zIndex={1}>
        {childrenTop}
        {!compact && <DocumentTypes documentTypes={documentTypes} documentTypeLabels={documentTypeLabels} />}
        <InternalLinkOverlay
          id={`bibbi-work:${id}`}
          href={path}
          _groupHover={{ textDecoration: "underline" }}
          ref={ref}
        >
          <Heading
            sx={{ WebkitBoxOrient: "vertical", WebkitLineClamp: 4 }}
            overflow="hidden"
            textOverflow="ellipsis"
            maxWidth="100%"
            as="h3"
            fontSize="lg"
            display="-webkit-box"
            flexDirection="column"
            wordBreak="break-word"
            lang={
              representativeTitleData && representativeTitleData?.titleLanguagesSignature !== lang
                ? representativeTitleData?.titleLanguagesSignature
                : undefined
            }
            {...headingProps}
          >
            {title}
          </Heading>
        </InternalLinkOverlay>
        {mainContributorAgent && (
          <TruncatedList items={[mainContributorAgent]} max={2} _hover={{ textDecoration: "none !important" }} />
        )}
        {!compact && formattedLanguages && formattedLanguages.length > 0 && (
          <TruncatedList
            items={formattedLanguages.map((language) => ({ name: language }))}
            max={3}
            fontSize="sm"
            color={colors.grey60}
          />
        )}
        {!compact && <Collections fontSize="sm" collections={workTriple?.collections} />}
        {childrenBottom}
      </VStack>
      <ItemCoverImage
        item={work ?? publication}
        maxWidth={compact ? "5rem" : "7rem"}
        minWidth={compact ? "5rem" : "7rem"}
        borderRadius="lg"
        showFavoriteButton
      />
    </LinkBox>
  );
};

export default ItemPreview;
